import * as angular from 'angular';

import { DiscreteValueOption, ReportTypes } from '@mode/shared/contract-common';
import { MultiValueFilterControl, MultiValueSelection } from '@mode/filters/util';
import { Set } from 'immutable';
import { SelectionMode } from '@mode-vegas/vegas';
import { generateUUID } from '@mode/shared/util-js';

const DEFAULT_OPTION_COUNT_SEARCH_MIN = 12;

export default class FilterModuleMultiController implements angular.IController {
  static $inject = [];

  filter: MultiValueFilterControl;
  filterOpts: ReportTypes.MultiSelectFilterOptions;
  filterOptsSet: Set<ReportTypes.ReportFilterValue>;
  searchMin?: number;
  selected: MultiValueSelection;
  options: DiscreteValueOption[];
  vizFilter?: boolean;
  showExcludeToggle?: boolean;
  position: string;

  constructor() {
    // remove dashes from uuid to avoid any potenital naming collisions
    this.position = generateUUID().replace(/-/g, '');
  }

  // Interaction
  onSelection: ($args: {
    $filter: MultiValueFilterControl;
    $selection: ReportTypes.DiscreteFilterSelectionTypes;
  }) => void;

  filterPattern: '';

  $onChanges($changes: angular.IOnChangesObject): void {
    if ($changes.filter && $changes.filter.currentValue != null) {
      this.selected = this.filter.selected;
    }

    if ($changes.filterOpts && this.filterOpts != null) {
      this.options = this.filterOpts.values.map((o) => new DiscreteValueOption(o));
      this.filterOptsSet = Set(this.filterOpts.values.map((v) => v.value));
    }
  }

  isSelected(option: ReportTypes.ReportFilterValue) {
    if (this.selected.all) {
      return true;
    } else {
      return this.selected.values.has(option);
    }
  }

  isPartiallySelected(): boolean {
    return !this.selected.all && this.selected.values.size > 0;
  }

  toggleSelection(value: ReportTypes.ReportFilterValue, checked: boolean) {
    /**
     * This logic is transitional. It remains so the checkbox can be updated in realtime until later,
     * when everything is in place for the logic to be done in a centralized location.
     */
    const all = this.filterOpts.values.length === this.selected.values.count() + (checked ? 1 : -1);
    if (!all && this.selected.all) {
      this.selected = this.selected.with({
        values: this.filterOptsSet,
        selectionMode: this.excludeValues ? SelectionMode.EXCLUDE : SelectionMode.INCLUDE,
      });
    }

    if (all) {
      this.selected = this.selected.with({
        all: true,
        values: this.selected.values.clear(),
        selectionMode: this.excludeValues ? SelectionMode.EXCLUDE_ALL : SelectionMode.ALL,
      });
    } else {
      this.selected = this.selected.with({
        all: false,
        values: checked ? this.selected.values.add(value) : this.selected.values.remove(value),
        selectionMode: this.excludeValues ? SelectionMode.EXCLUDE : SelectionMode.INCLUDE,
      });
    }

    this.onSelection({
      $filter: this.filter,
      $selection: { value, selected: checked, type: ReportTypes.DiscreteSelectionType.Select },
    });
  }

  get selectAll() {
    return this.selected.all;
  }
  set selectAll(ignored: boolean) {
    this.selected = this.selected.with({
      all: !this.selected.all && !this.isPartiallySelected(),
      values: this.selected.values.clear(),
    });

    this.onSelection({
      $filter: this.filter,
      $selection: {
        type: this.selected.all ? ReportTypes.DiscreteSelectionType.All : ReportTypes.DiscreteSelectionType.None,
      },
    });
  }

  toggleExcludeValues(state: boolean) {
    this.excludeValues = state;
  }

  get excludeValues(): boolean {
    return (
      this.selected.selectionMode === SelectionMode.EXCLUDE || this.selected.selectionMode === SelectionMode.EXCLUDE_ALL
    );
  }

  set excludeValues(checked: boolean) {
    if (checked !== this.excludeValues) {
      if (checked) {
        this.selected = this.selected.with({
          selectionMode: this.selected.all ? SelectionMode.EXCLUDE_ALL : SelectionMode.EXCLUDE,
        });

        this.onSelection({
          $filter: this.filter,
          $selection: {
            type: this.selected.all
              ? ReportTypes.DiscreteSelectionType.ExcludeAll
              : ReportTypes.DiscreteSelectionType.Exclude,
          },
        });
      } else {
        this.selected = this.selected.with({
          selectionMode: this.selected.all ? SelectionMode.ALL : SelectionMode.INCLUDE,
        });

        this.onSelection({
          $filter: this.filter,
          $selection: {
            type: ReportTypes.DiscreteSelectionType.Include,
          },
        });
      }
    }
  }

  get isAllSelectable() {
    return this.filter.hideSelectAll !== true;
  }

  minimumOptionsAvailable(): boolean {
    const searchMin = typeof this.searchMin === 'number' ? this.searchMin : DEFAULT_OPTION_COUNT_SEARCH_MIN;
    return this.options?.length >= searchMin;
  }
}
